import Head from 'next/head';
import { useRouter } from 'next/router';

type SEOMetaProps = {
    title?: string;
    description?: string;
    keywords?: string;
    img?: string;
    url?: string;
    noindex?: boolean;
    structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
}

export const SEOMeta: React.FC<SEOMetaProps> = ({ title, description, keywords, img, url, noindex = false, structuredData }) => {
    const siteUrl = "https://afivio.com";
    const router = useRouter();

    const canonicalPath = (() => {
        let path = url || router.asPath || "/";
        if (path.startsWith("http://") || path.startsWith("https://")) {
            try {
                path = new URL(path).pathname || "/";
            } catch {
                path = "/";
            }
        }
        if (!path.startsWith("/")) path = `/${path}`;
        path = path.split("?")[0].split("#")[0].replace(/\/{2,}/g, "/");
        if (path !== "/") path = path.replace(/\/+$/, "");
        return path || "/";
    })();

    const abs = (p?: string) => {
        if (!p) return `${siteUrl}/`;
        if (p.startsWith("http://") || p.startsWith("https://")) return p;
        if (p.startsWith("/")) return `${siteUrl}${p}`;
        return `${siteUrl}/${p}`;
    };

    const locale = router.locale === 'pl' || router.locale === 'uk' ? router.locale : 'en';
    const utilityPath = router.pathname.startsWith('/dashboard')
        || router.pathname.startsWith('/email/')
        || router.pathname === '/shopify'
        || router.pathname === '/reset-password'
        || router.pathname === '/signin'
        || router.pathname === '/signup';
    const shouldNoindex = noindex || utilityPath;
    const localeNeutralCanonicalPath = canonicalPath.replace(/^\/(en|pl|uk)(?=\/|$)/, '') || '/';
    const localePath = locale === 'en'
        ? localeNeutralCanonicalPath
        : localeNeutralCanonicalPath === '/'
            ? `/${locale}`
            : `/${locale}${localeNeutralCanonicalPath}`;
    const canonical = localePath === "/" ? `${siteUrl}/` : `${siteUrl}${localePath}`;
    const ogImage = abs("/og.png");
    const canonicalPathname = (() => {
        try {
            const u = new URL(canonical);
            return u.pathname || "/";
        } catch {
            return "/";
        }
    })();
    const localeNeutralPath = canonicalPathname.replace(/^\/(en|pl|uk)(?=\/|$)/, "") || "/";
    const buildLocaleHref = (locale: "en" | "pl" | "uk") => {
        if (locale === "en") {
            return localeNeutralPath === "/" ? `${siteUrl}/` : `${siteUrl}${localeNeutralPath}`;
        }
        return localeNeutralPath === "/"
            ? `${siteUrl}/${locale}`
            : `${siteUrl}/${locale}${localeNeutralPath}`;
    };

    const breadcrumbItems = canonicalPathname === "/"
        ? [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": `${siteUrl}/`
            }
        ]
        : [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": `${siteUrl}/`
            },
            {
                "@type": "ListItem",
                "position": 2,
                "name": title ?? "Afivio",
                "item": canonical
            }
        ];

    let setTitle = title ?? "Affiliate Marketing Software & Referral Platform | Afivio";
    let setDescription = description ?? "Affiliate marketing software for tracking, referral campaigns, commission rules, and automated payouts.";
    let setKeywords = keywords ?? "Afivio, marketing platform, referral campaign software, partner program management, SaaS affiliate, ecommerce referral program, create referral program";
    const setImg = img ? abs(img) : ogImage;
    const breadcrumbId = `${canonical}#breadcrumb`;

    return (
        <Head>
            <meta key="utfType" charSet="utf-8" />
            <meta key="httpEquiv" httpEquiv="X-UA-Compatible" content="IE=edge" />
            <meta key="description" name="description" content={setDescription} />
            <meta key="keywords" name="keywords" content={setKeywords} />
            <meta name="robots" content={shouldNoindex ? "noindex,follow" : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image"/>
            <meta name="twitter:title" content={setTitle}/>
            <meta name="twitter:description" content={setDescription}/>
            <meta name="twitter:image" content={setImg}/>
            <meta name="twitter:creator" content="@useafivio" />

            {/* Open Graph */}
            <meta key="ogURL" property="og:url" content={canonical} />
            <meta key="ogImage" property="og:image" content={setImg} />
            <meta key="ogSiteName" property="og:site_name" content="afivio" />
            <meta key="ogType" property="og:type" content="website" />
            <meta key="ogTitle" property="og:title" content={setTitle} />
            <meta key="ogDescription" property="og:description" content={setDescription} />
            <meta key="themeColor" name="theme-color" content="#ffaf45" />
            <title key="title">{setTitle}</title>

            <link rel="manifest" href="/site.webmanifest"/>
            <link
                href="/favicon-black.webp"
                rel="icon"
                type="webp"
                sizes="16x16"
            />
            <link
                href="/favicon-black.webp"
                rel="icon"
                type="webp"
                sizes="32x32"
            />

            <link rel="canonical" href={canonical} />
            <link rel="alternate" hrefLang="en" href={buildLocaleHref("en")} />
            <link rel="alternate" hrefLang="pl" href={buildLocaleHref("pl")} />
            <link rel="alternate" hrefLang="uk" href={buildLocaleHref("uk")} />
            <link rel="alternate" hrefLang="x-default" href={buildLocaleHref("en")} />

            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify({
                        "@context": "https://schema.org",
                        "@graph": [
                            {
                                "@type": "Organization",
                                "@id": `${siteUrl}/#organization`,
                                "name": "Afivio",
                                "url": `${siteUrl}/`,
                                "email": "contact@afivio.com",
                                "sameAs": [
                                    "https://www.instagram.com/afivio_com",
                                    "https://www.facebook.com/profile.php?id=61579156503707",
                                    "https://www.linkedin.com/company/afivio"
                                ],
                                "contactPoint": [{
                                    "@type": "ContactPoint",
                                    "contactType": "customer support",
                                    "email": "contact@afivio.com",
                                    "availableLanguage": ["en", "pl", "uk"]
                                }],
                                "logo": {
                                    "@type": "ImageObject",
                                    "url": `${siteUrl}/logo_main.png`,
                                    "width": 512,
                                    "height": 512
                                }
                            },
                            {
                                "@type": "WebSite",
                                "@id": `${siteUrl}/#website`,
                                "url": `${siteUrl}/`,
                                "name": "Afivio",
                                "alternateName": ["Afivio Affiliate Platform", "Afivio Referral Platform"],
                                "publisher": { "@id": `${siteUrl}/#organization` }
                            },
                            {
                                "@type": "WebPage",
                                "@id": `${canonical}#webpage`,
                                "url": canonical,
                                "name": setTitle,
                                "description": setDescription,
                                "isPartOf": { "@id": `${siteUrl}/#website` },
                                "inLanguage": locale,
                                "breadcrumb": { "@id": breadcrumbId }
                            },
                            {
                                "@type": "BreadcrumbList",
                                "@id": breadcrumbId,
                                "itemListElement": breadcrumbItems
                            },
                            {
                                "@type": "SiteNavigationElement",
                                "@id": `${siteUrl}/#primary-navigation`,
                                "name": "Main Navigation",
                                "url": `${siteUrl}/`,
                                "potentialAction": {
                                    "@type": "Action",
                                    "name": "Navigate to page",
                                    "target": [
                                        `${siteUrl}/about`,
                                        `${siteUrl}/how-it-works`,
                                        `${siteUrl}/pricing`,
                                        `${siteUrl}/blog`
                                    ]
                                }
                            }
                        ]
                    })
                }}
            />
            {structuredData ? (
                <script
                    key="page-structured-data"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
                />
            ) : null}

        </Head>
    )
};

export default SEOMeta;
